import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth , db } from './firebase/firebaseConfig';
import Presentation from './components/presentation/Presentation';

function Authentication({project}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showDeviceNameInput, setShowDeviceNameInput] = useState(false);
  const [isDeviceRegistered, setIsDeviceRegistered] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorStyle, setErrorStyle] = useState({});

  useEffect(() => {
    // Check if deviceId exists in local storage
    const storedDeviceId = localStorage.getItem('deviceId');
    if (storedDeviceId) {
      setDeviceId(storedDeviceId);
    }

    // Check authentication state
    onAuthStateChanged(auth, (user) => {
      if (user) {
        checkDeviceActivation(user.email);
      } else {
        setIsLoggedIn(false);
      }
    });
  }, []);

  const checkDeviceActivation = async (email) => {
    try {
      const userRef = doc(db, `projects/${project}/users`, email);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const devices = userDoc.data().devices;
        if (devices && Object.values(devices).some(device => device.deviceId === deviceId)) {
          const deviceNameRegistered = Object.keys(devices).find(key => devices[key].deviceId === deviceId);
          setDeviceName(deviceNameRegistered);
          setIsDeviceRegistered(true);
          setShowDeviceNameInput(false);
          const deviceData = devices[deviceNameRegistered];
          if (deviceData.activated) {
            setIsLoggedIn(true);
          } else {
            setErrorMessage(`This device (${deviceNameRegistered}) has not been activated, please contact Admin.`);
            setErrorStyle({
              backgroundColor: 'rgba(255, 165, 0, 0.2)',
              color: 'darkorange',
              padding: '10px',
              borderRadius: '5px',
              textAlign: 'center',
              marginTop: '10px',
            });
            handleLogout();
          }
        } else {
          setShowDeviceNameInput(true);
        }
      }
    } catch (error) {
      console.error('Error checking device activation:', error);
    }
  };

  const handleLogin = async () => {
    try {
      // Clear device name input and hide register device button
      setDeviceName('');
      setShowDeviceNameInput(false);
      setIsDeviceRegistered(false);
      setErrorMessage('');
      setErrorStyle({});

      // Sign in the user
      await signInWithEmailAndPassword(auth, username, password);

      console.log("logged")

      // Generate a new device ID if one does not exist
      if (!deviceId) {
        const newDeviceId = uuidv4();
        setDeviceId(newDeviceId);
        localStorage.setItem('deviceId', newDeviceId);
      }

      // Reference to the user document under the project
      const userRef = doc(db, `projects/${project}/users`, username);
      const userDoc = await getDoc(userRef);

      const currentLocation = window.location.href;
      const currentDate = new Date().toISOString();

      if (!userDoc.exists()) {
        // User document doesn't exist, prompt for device name
        setShowDeviceNameInput(true);
      } else {
        // Check if the device is already registered
        const devices = userDoc.data().devices;
        if (!devices || !Object.values(devices).some(device => device.deviceId === deviceId)) {
          // Device is not registered, prompt for device name
          setShowDeviceNameInput(true);
        } else {
          // Device is already registered
          const deviceNameRegistered = Object.keys(devices).find(key => devices[key].deviceId === deviceId);
          setDeviceName(deviceNameRegistered);
          setIsDeviceRegistered(true);
          setShowDeviceNameInput(false);
          const deviceData = devices[deviceNameRegistered];
          if (deviceData.activated) {
            // Update the last login date
            await updateDoc(userRef, {
              [`devices.${deviceNameRegistered}.lastLogin`]: currentDate,
            });
            setIsLoggedIn(true);
          } else {
            setErrorMessage(`This device (${deviceNameRegistered}) has not been activated, please contact Admin.`);
            setErrorStyle({
              backgroundColor: 'rgba(255, 165, 0, 0.2)',
              color: 'darkorange',
              padding: '10px',
              borderRadius: '5px',
              textAlign: 'center',
              marginTop: '10px',
            });
            handleLogout();
          }
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      setErrorMessage('The login credentials are incorrect.');
      setErrorStyle({
        backgroundColor: 'rgba(255, 0, 0, 0.2)',
        color: 'red',
        padding: '10px',
        borderRadius: '5px',
        textAlign: 'center',
        marginTop: '10px',
      });
    }
  };

  const handleRegisterDevice = async () => {
    if (!deviceName.trim()) {
      alert('Device name is required.');
      return;
    }

    try {
      const userRef = doc(db, `projects/${project}/users`, username);
      const userDoc = await getDoc(userRef);

      const currentLocation = window.location.href;
      const currentDate = new Date().toISOString();

      if (!userDoc.exists()) {
        // Create user document with the current device name and device ID
        await setDoc(userRef, {
          devices: {
            [deviceName]: {
              deviceId,
              activated: false,
              dateAdded: currentDate,
              lastLogin: currentDate,
              locationAdded: currentLocation,
            },
          },
        });
      } else {
        // Check if the device ID is already registered
        const devices = userDoc.data().devices;
        if (devices && Object.values(devices).some(device => device.deviceId === deviceId)) {
          alert('This device is already registered under your account.');
          handleLogout();
          return;
        }

        // Update the user document to add the new device
        await updateDoc(userRef, {
          [`devices.${deviceName}`]: {
            deviceId,
            activated: false,
            dateAdded: currentDate,
            lastLogin: currentDate,
            locationAdded: currentLocation,
          },
        });
      }

      setIsDeviceRegistered(true);
      setErrorMessage(`This device (${deviceName}) has not been activated, please contact Admin.`);
      setErrorStyle({
        backgroundColor: 'rgba(255, 165, 0, 0.2)',
        color: 'darkorange',
        padding: '10px',
        borderRadius: '5px',
        textAlign: 'center',
        marginTop: '10px',
      });
      handleLogout();
    } catch (error) {
      console.error('Device registration failed:', error);
      alert('Failed to register device. Please try again.');
    }
  };

  const handleLogout = () => {
    signOut(auth);
    setIsLoggedIn(false);
  };

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
    // Clear device name input and hide register device button when username or password changes
    setDeviceName('');
    setShowDeviceNameInput(false);
    setIsDeviceRegistered(false);
    setErrorMessage('');
    setErrorStyle({});
  };

  useEffect(() => {
    console.log(isLoggedIn)
  }, [isLoggedIn]);

  if (isLoggedIn) {
    return (
      <Presentation/>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-black via-gray-900 to-gray-800 p-4 sm:p-6">
      <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_20%_20%,#f59e0b_0,transparent_25%),radial-gradient(circle_at_80%_80%,#eab308_0,transparent_30%)]"></div>
      <div className="relative grid w-full max-w-5xl overflow-hidden rounded-2xl border border-yellow-500/20 bg-black/80 shadow-2xl backdrop-blur-sm md:grid-cols-2">
        <div className="relative hidden md:flex flex-col justify-between border-r border-yellow-500/20 bg-gradient-to-br from-black to-gray-900 p-8">
          <div>
            <div className="mb-4 inline-flex items-center rounded-full border border-yellow-400/40 bg-yellow-500/10 px-3 py-1 text-xs font-semibold tracking-wide text-yellow-300">
              SPRING MINE
            </div>
            <h2 className="text-3xl font-bold leading-tight text-white">3DMA Presentation Portal</h2>
            <p className="mt-3 text-sm text-gray-300">Secure access to models, insights and mine presentation scenes.</p>
          </div>
          <img src="./presentation/3DMA.jpg" alt="SPRING Mine" className="mt-6 h-72 w-full rounded-xl border border-yellow-500/20 object-cover shadow-xl" />
        </div>
        <div className="p-6 sm:p-8 md:p-10">
          <div className="mb-8 flex flex-col items-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 shadow-lg">
              <span className="text-lg font-extrabold text-black">3DMA</span>
            </div>
            <h1 className="text-center text-2xl font-bold tracking-wide text-yellow-400 sm:text-3xl">PRESENTING SPRING MINE</h1>
          </div>
          <div className="space-y-4">
            <input
              type="email"
              placeholder="Username"
              value={username}
              onChange={handleInputChange(setUsername)}
              className="w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-white placeholder-gray-400 shadow-sm transition focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={handleInputChange(setPassword)}
              className="w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-white placeholder-gray-400 shadow-sm transition focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
            />
            <button
              onClick={handleLogin}
              className="w-full rounded-lg bg-gradient-to-r from-yellow-500 to-yellow-600 px-4 py-3 text-sm font-bold text-black shadow-lg transition hover:from-yellow-400 hover:to-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
            >
              Login
            </button>
            {showDeviceNameInput && !isDeviceRegistered && (
              <div className="mt-2 space-y-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
                <input
                  type="text"
                  placeholder="Device Name"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  className="w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-white placeholder-gray-400 shadow-sm transition focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
                  disabled={isDeviceRegistered}
                />
                <button
                  onClick={handleRegisterDevice}
                  className="w-full rounded-lg border border-yellow-500/60 bg-transparent px-4 py-3 text-sm font-semibold text-yellow-300 shadow-sm transition hover:bg-yellow-500/15 focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
                >
                  Register Device
                </button>
              </div>
            )}
            {errorMessage && (
              <div style={errorStyle} className="rounded-lg text-center text-sm font-medium">{errorMessage}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Authentication;
